import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, FileText, Building, User, Server, ArrowLeft, Mail, MapPin } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const LegalNotice = () => {
  const { contactEmail } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">Phoelynx France</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
            <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
            <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
            <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
              <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
              <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
              <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-600/20 to-sky-600/20">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">Mentions Légales</h1>
          </div>

          <p className="text-gray-400 text-lg">
            Informations légales et réglementaires
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">

          {/* Identification */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <Building className="w-6 h-6 text-red-400" />
              Identification de l'entreprise
            </h2>
            <div className="text-gray-300 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-white mb-2">Dénomination</h3>
                  <p>Phoelynx France</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Forme juridique</h3>
                  <p>Micro-entreprise (Auto-entrepreneur)</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">SIREN</h3>
                  <p>991540220</p>
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Code APE</h3>
                  <p>8559B - Autres enseignements</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-semibold text-white mb-2">Adresse du siège social</h3>
                <p>Saint-Nexans, 24520 Dordogne, France</p>
              </div>
            </div>
          </div>

          {/* Responsable publication */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <User className="w-6 h-6 text-red-400" />
              Directeur de publication
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>Le directeur de la publication du site phoelynx.fr est le gérant de l'entreprise Phoelynx France.</p>

              <div className="bg-black/50 p-4 rounded-lg">
                <h3 className="font-semibold text-white mb-3">Contact</h3>
                <div className="space-y-2">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-red-400" />
                    {contactEmail}
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-400" />
                    Saint-Nexans, Dordogne
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hébergement */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <Server className="w-6 h-6 text-red-400" />
              Hébergement
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>Le site web phoelynx.fr est hébergé par :</p>

              <div className="bg-black/50 p-4 rounded-lg">
                <h3 className="font-semibold text-white mb-2">Serveurs Propres</h3>
                <p>Serveurs web et DNS privés</p>
                <p className="mt-2">Hébergement auto-géré en France 🇫🇷</p>
              </div>
            </div>
          </div>

          {/* Propriété intellectuelle */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Propriété intellectuelle</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle.
                Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques et photographiques.
              </p>

              <p>
                La marque "Phoelynx" ainsi que les logos, textes, images, vidéos et autres éléments distinctifs du site sont la propriété exclusive de Phoelynx France et sont protégés par les dispositions du Code de la propriété intellectuelle.
              </p>

              <div className="bg-red-900/20 p-4 rounded-lg">
                <h3 className="font-semibold text-white mb-2">Utilisation autorisée</h3>
                <p className="text-sm">
                  Toute reproduction ou représentation totale ou partielle de ce site par quelque procédé que ce soit,
                  sans l'autorisation expresse de Phoelynx France est interdite et constituerait une contrefaçon
                  sanctionnée par les articles L 335-2 et suivants du Code de la propriété intellectuelle.
                </p>
              </div>
            </div>
          </div>

          {/* Données personnelles */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Protection des données personnelles</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Conformément à la loi "Informatique et Libertés" du 6 janvier 1978 modifiée et au Règlement Général sur la Protection des Données (RGPD),
                vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition aux données personnelles vous concernant.
              </p>

              <p>
                Pour exercer ces droits ou pour toute question sur le traitement de vos données dans ce dispositif,
                vous pouvez nous contacter à l'adresse : {contactEmail}
              </p>

              <div className="text-center">
                <Link to="/politique-confidentialite" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-sky-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all">
                  Consulter notre politique de confidentialité
                </Link>
              </div>
            </div>
          </div>

          {/* Responsabilité */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Limitation de responsabilité</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Phoelynx France s'efforce de fournir sur le site phoelynx.fr des informations aussi précises que possible.
                Toutefois, il ne pourra être tenu responsable des omissions, des inexactitudes et des carences dans la mise à jour,
                qu'elles soient de son fait ou du fait des tiers partenaires qui lui fournissent ces informations.
              </p>

              <p>
                Tous les informations indiquées sur le site phoelynx.fr sont données à titre indicatif,
                et sont susceptibles d'évoluer. Par ailleurs, les renseignements figurant sur le site phoelynx.fr
                ne sont pas exhaustifs. Ils sont donnés sous réserve de modifications ayant été apportées depuis leur mise en ligne.
              </p>
            </div>
          </div>

          {/* Droit applicable */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Droit applicable et juridiction</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Tout litige en relation avec l'utilisation du site phoelynx.fr est soumis au droit français.
                Il est fait attribution exclusive de juridiction aux tribunaux compétents de Périgueux (24).
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3 group">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-10 h-10 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
              />
              <div>
                <span className="text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">PHOELYNX FRANCE</span>
                <p className="text-xs text-gray-400">Micro-entreprise • SIREN 991540220</p>
              </div>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <span className="text-red-400">Mentions légales</span>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-red-400 transition-colors">
                  Confidentialité
                </Link>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-red-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalNotice;